<template>
  <div class="modal" :class="{'is-active': showFormModal}">
    <div class="modal-background" @click.stop="$emit('close')"></div>
    <div class="modal-content">
      <div class="box modal-container" style="padding: 30px;">
        <div class="modal-content-root" style="margin-top: 0;">
          <p class="title-font is-size-4 mainColor--text">
            Tell us more about this page
          </p>
          <div class="mainColor--text message-text">

            <div class="mb-6" style="font-size: 1rem; line-height: 1.6;">Tell us a bit more about this page (type, audience, product, etc.).<br/>The more context you give, the more precise our optimization recommendations will be.</div>

            <div class="mb-6">

              <b-collapse
                class="configs-collapsible card"
                animation="slide"
                v-for="(item, index) in configComponents"
                :key="`Config${item.key}`"
                :open="isOpenCustomConfig === index"
                @open="isOpenCustomConfig = index"
              >
                <template #trigger="props">
                    <div
                        class="card-header"
                        role="button"
                        :aria-controls="'contentIdForA11y5-' + index"
                        :aria-expanded="props.open"
                    >
                        <div class="card-header-title">
                            <div class="config-left">
                              <div :class="{
                                'config-status': true,
                                [`is-${item.status}`]: true,
                              }">
                                <i v-if="item.status === 'selected'" class="material-icons-outlined">done</i>
                                <i v-else-if="item.status === 'error'"  class="material-icons-outlined">priority_high</i>
                              </div>
                              <span>{{ item.label }}</span>
                              <span v-if="item.required" class="required-label">
                                Required
                              </span>
                            </div>
  
                            <component
                              :is="`ConfigPreview${item.key}`"
  
                              :webPageTypeOptions="webPageTypeOptions"
                              :webPageType="webPageType"

                              :productDescription="productDescription"
                              :targetAudience="targetAudience"
                              :toneOfContent="toneOfContent"
                              :author="author"
                              :brand="brand"
                              :excludeTerms="excludeTerms"
                            />
  
                        </div>
                        <a class="card-header-icon">
                          <i class="material-icons-outlined">{{ props.open ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}</i>
                        </a>
                    </div>
                </template>
                <div class="card-content">
                    <div class="content">
                        <component
                          :is="`Config${item.key}`"
                          
                          :countWords="countWords"
  
                          :webPageTypeOptions="webPageTypeOptions"
                          :webPageType.sync="webPageType"
                          
                          :productDescription.sync="productDescription"
  
                          :targetAudience.sync="targetAudience"
    
                          :toneOfContent.sync="toneOfContent"
                          :author.sync="author"
                          :brand.sync="brand"
  
                          :excludeTerms.sync="excludeTerms"
                          :addNewExcludeTerm="addNewExcludeTerm"
                          :handleRemoveExcludeTerm="handleRemoveExcludeTerm"
  
                        />
                    </div>
                </div>
              </b-collapse>

            </div>

          </div>

          <div class="d-flex align-center justify-end gap10">
            <v-btn
              outlined
              color="mainColor"
              class="button-with-icon mainColor--text text-none"
              style="border-color: var(--v-grayColor-base);"
              v-if="existingOrdersCount <= 5"
              @click="() => {
                $store.commit('setCurrentVideoTutorialToDisplay', {
                  title: 'Premium Review Optimization Report - Overview',
                  videoLink: 'https://www.youtube.com/embed/t3jR5guBUYU',
                  description: 'In this video, we walk you through the new Premium Optimization Review in POP. This report is a \'gift\' for non-SEO professionals, clients, or SEO assistants who find standard content briefs a bit tricky to implement. We show you how this report transforms complex data into easy-to-follow instructions to improve the SEO of your already existing web pages. Note: This report is specifically for improving the SEO of existing, published web pages.',
                  time: '1m 29s'
                });
              }"
            >
              <span class="mr-1" style="display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; border-radius: 100%; background-color: var(--v-profileAnchorColor-base);">
                <i class="material-icons" style="color: #fff; font-size: 0.78rem; margin-bottom: 1px;">videocam</i>
              </span>
              <span style="font-size: 0.8rem; font-weight: 700;">Sample report</span>
            </v-btn>
            <v-btn
              v-if="activeOrder?.order_status === 'user_editing'"
              color="profileAnchorColor"
              class="button-with-icon whiteColor--text text-none"
              @click="confirmOrder()"
              :disabled="!isFormValid"
            >
              <span style="font-size: 0.85rem;">Order for {{ credits_required }} POP Credits</span>
            </v-btn>
          </div>
        </div>

      </div>
      <button
        class="custom-modal-close-btn"
        aria-label="close"
        @click="$emit('close')">
        <i class="material-icons">close</i>
      </button>
    </div>
  </div>
</template>

<script>
import ConfigWebPageType from "./configs/web-page-type/ConfigWebPageType.vue";
import ConfigPreviewWebPageType from "./configs/web-page-type/ConfigPreviewWebPageType.vue";

import ConfigProductDescription from "./configs/product-description/ConfigProductDescription.vue";
import ConfigPreviewProductDescription from "./configs/product-description/ConfigPreviewProductDescription.vue";

import ConfigTargetAudience from "./configs/target-audience/ConfigTargetAudience.vue";
import ConfigPreviewTargetAudience from "./configs/target-audience/ConfigPreviewTargetAudience.vue";

import ConfigTone from "./configs/tone/ConfigTone.vue";
import ConfigPreviewTone from "./configs/tone/ConfigPreviewTone.vue";

import ConfigExcludeTerms from "./configs/exclude-terms/ConfigExcludeTerms.vue";
import ConfigPreviewExcludeTerms from "./configs/exclude-terms/ConfigPreviewExcludeTerms.vue";


const configComponents = [
  {
    label: 'Page type',
    key: 'WebPageType',
    component: ConfigWebPageType,
    previewComponent: ConfigPreviewWebPageType,
    required: true,
  },
  {
    label: 'Product description',
    key: 'ProductDescription',
    component: ConfigProductDescription,
    previewComponent: ConfigPreviewProductDescription,
  },
  {
    label: 'Target audience',
    key: 'TargetAudience',
    component: ConfigTargetAudience,
    previewComponent: ConfigPreviewTargetAudience,
  },
  {
    label: 'Tone of voice',
    key: 'Tone',
    component: ConfigTone,
    previewComponent: ConfigPreviewTone,
  },
  {
    label: 'Exclude specific words',
    key: 'ExcludeTerms',
    component: ConfigExcludeTerms,
    previewComponent: ConfigPreviewExcludeTerms,
  },
]

export default {
  props: [
    "showFormModal",
    "pageRun",
    "score",
    "credits_required",
    "activeOrder",
    "existingOrdersCount",
  ],
  components: {
    ...Object.fromEntries(configComponents.map(item => [`Config${item.key}`, item.component])),
    ...Object.fromEntries(configComponents.map(item => [`ConfigPreview${item.key}`, item.previewComponent]))
  },
  data() {
    return {
      loading: false,
      newOrderLoading: false,
      
      isOpenCustomConfig: null,

      productDescription: '',
      targetAudience: '',
      toneOfContent: '',
      author: '',
      brand: '',
      excludeTerms: [],
      webPageType: "",

      webPageTypeOptions: [
        {
          value: "article",
          text: "Article",
        },
        {
          value: "blog",
          text: "Blog post",
        },
        {
          value: "product",
          text: "Product page",
        },
        {
          value: "category",
          text: "Category page",
        },
        {
          value: "product-category",
          text: "Product category page",
        },
        {
          value: "service",
          text: "Service page",
        },
      ],
    };
  },
  computed: {
    configStatuses () {
      // 'selected', 'not-selected', 'error', undefined
      return {
        'WebPageType': this.webPageType ? 'selected' : 'error',
        'ProductDescription': this.productDescription ? 'selected' : 'not-selected',
        'TargetAudience': this.targetAudience ? 'selected' : 'not-selected',
        'Tone': this.toneOfContent || this.author || this.brand ? 'selected' : 'not-selected',
        'ExcludeTerms': this.excludeTerms?.length ? 'selected' : 'not-selected',
      }
    },
    configComponents() {
      return configComponents.map((item) => ({
        ...item,
        status: this.configStatuses[item.key]
      }))
    },
    isFormValid() {
      return (
        Boolean(this.webPageType)
      )
    },
  },
  methods: {
    countWords(value) {
      return value?.split?.(' ')?.filter?.(x => x !== '')?.length || 0
    },
    addNewExcludeTerm(termsStr) {
      termsStr = termsStr.trim().toLowerCase();
      if (!termsStr) {
        return;
      }
      const excludeTerms = this.excludeTerms.slice();

      let terms = termsStr.split(",").map((item) => item.trim());

      terms.map((term) => {
        if (!excludeTerms.includes(term)) {
          excludeTerms.push(term);
        }
      });
      this.excludeTerms = excludeTerms;
    },
    handleRemoveExcludeTerm(p) {
      this.excludeTerms = this.excludeTerms.filter((term) => term != p);
    },
    reloadUser() {
      // reload user to update ui with any credit usage
      this.$store.dispatch("loadUser").then(response => {
        this.$store.commit("setUser", response.data.user);
      });
    },
    updateOrder(payload, silent = false) {
      if (!silent) {
        this.$store.commit('showLoading');
      }
      return this.$store.dispatch('updateReviewOptimizationOrder', {
        orderId: this.activeOrder.id,
        body: payload
      })
      .then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          this.$emit('refreshData', {resetActiveOrderId: false, silent: true});
        }
      })
      .finally(() => {
        if (!silent) {
          this.$store.commit('hideLoading');
        }
      })
    },
    confirmOrder() {
      this.$store.commit('showLoading');
      return this.updateOrder({
        orderId: this.activeOrder.id,

        productDescription: this.productDescription,
        targetAudience: this.targetAudience,
        toneOfContent: this.toneOfContent,
        author: this.author,
        brand: this.brand,
        excludeTerms: this.excludeTerms,
        webPageType: this.webPageType,

        confirm_order: true,  // important
      })
      .then(() => this.reloadUser())
      .then(() => {
        this.$emit('close');
        this.$notify({ group: 'info', type: 'success', text: 'Successfully confirmed order.' })
      })
      .finally(() => {
        this.$store.commit('hideLoading');
      })
    }
  },
  mounted() {
    if (this.activeOrder) {
      this.productDescription = this.activeOrder.productDescription
      this.targetAudience = this.activeOrder.targetAudience
      this.toneOfContent = this.activeOrder.toneOfContent
      this.author = this.activeOrder.author
      this.brand = this.activeOrder.brand
      this.excludeTerms = this.activeOrder.excludeTerms
      this.webPageType = this.activeOrder.webPageType
    }
  }
};
</script>

<style lang="scss" scoped>
.configs-collapsible {

  box-shadow: none;
  border: 0.5px solid var(--v-grayColor-base);
  background: var(--v-whiteColor-base);

  border-radius: 0.25rem;

  &:not(:last-child) {
    border-bottom: none;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &:not(:first-child) {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }


  .card-header {
    background-color: var(--v-whiteColor-base) !important;
    box-shadow: none;

    &[aria-expanded="true"] {
      background-color: var(--v-whiteColor-base) !important;
      color: var(--v-mainColor3-base);
    }
  }

  .card-header-title {
    padding: 1rem 1rem;
    color: var(--v-mainColor3-base);
    .config-left {
      width: 310px;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 12px;

      .required-label {
        background-color: var(--v-gray7Color-base);
        color: var(--v-gray2Color-base);
        text-transform: uppercase;
        border-radius: 3px;
        font-size: 0.7rem;
        padding: 2px 4px;
      }
    }
  }
}

.config-status {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 100%;
  border: 1px solid var(--v-gray12Color-base);

  i {
    color: #fff;
    font-size: 0.8rem;
  }

  &.is-selected {
    background-color: #38C98C;
    border: none;
  }

  &.is-error {
    background-color: #FF1F2D;
    border: none;
  }
}
</style>
